import React, { forwardRef } from 'react';
import { cn } from '@/lib/utils';

/* ─── Types ─────────────────────────────────────────────────── */
export type SkeletonVariant = 'text' | 'circular' | 'rectangular';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  lines?: number;
}

const variantStyles: Record<SkeletonVariant, string> = {
  text: 'rounded-md',
  circular: 'rounded-full',
  rectangular: 'rounded-lg',
};

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant = 'text', width, height, lines, style, ...props }, ref) => {
    if (variant === 'text' && lines && lines > 1) {
      return (
        <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
          {Array.from({ length: lines }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'animate-pulse bg-gray-200 dark:bg-gray-700 rounded-md',
                i === lines - 1 && 'w-3/4'
              )}
              style={{
                width: i === lines - 1 ? '75%' : width ?? '100%',
                height: height ?? '1em',
                ...style,
              }}
            />
          ))}
        </div>
      );
    }

    const defaultHeight = variant === 'text' ? '1em' : variant === 'circular' ? width ?? '2.5rem' : '6rem';

    return (
      <div
        ref={ref}
        className={cn(
          'animate-pulse bg-gray-200 dark:bg-gray-700',
          variantStyles[variant],
          className
        )}
        style={{
          width: width ?? (variant === 'text' ? '100%' : undefined),
          height: height ?? defaultHeight,
          ...(variant === 'circular' && !height ? { aspectRatio: '1' } : {}),
          ...style,
        }}
        aria-hidden="true"
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';
